/**
 * GTM Enhanced E-commerce Tracking Composable
 *
 * Provides client-side event tracking for cart and checkout actions.
 */

// Get tracking settings from the page (injected by backend)
const trackingSettings = window._gtmTracking || {};

/**
 * Check if a specific tracking event is enabled
 */
function isTrackingEnabled(eventType) {
    return trackingSettings[eventType] === true || trackingSettings[eventType] === '1' || trackingSettings[eventType] === 1;
}

/**
 * Push event to dataLayer
 */
function pushToDataLayer(eventData) {
    if (!window.dataLayer) {
        window.dataLayer = [];
    }
    window.dataLayer.push(eventData);
}

/**
 * Format product data for GA4
 */
function formatProduct(product, quantity = null) {
    return {
        item_id: product.product_id || product.id,
        item_name: product.product_name || product.name,
        item_brand: window._tenantName || '',
        price: parseFloat(product.price || 0),
        quantity: quantity || product.quantity || 1,
        item_variant: product.variant_name || product.variant || null,
    };
}

/**
 * Track add to cart event
 */
export function trackAddToCart(product, quantity = 1) {
    console.log('🔍 trackAddToCart called', {
        product,
        quantity,
        trackingSettings,
        isEnabled: isTrackingEnabled('add_to_cart')
    });

    if (!isTrackingEnabled('add_to_cart')) {
        console.warn('⚠️ add_to_cart tracking is disabled');
        return;
    }

    const productData = formatProduct(product, quantity);

    const eventData = {
        event: 'add_to_cart',
        ecommerce: {
            currency: 'AUD',
            value: productData.price * productData.quantity,
            items: [productData],
        },
    };

    pushToDataLayer(eventData);

    console.group('🏷️ GTM Event: Add to Cart');
    console.log('Event Data:', eventData);
    console.log('Timestamp:', new Date().toISOString());
    console.groupEnd();
}

/**
 * Track remove from cart event
 */
export function trackRemoveFromCart(cartItem) {
    if (!isTrackingEnabled('remove_from_cart')) return;

    const productData = formatProduct(cartItem);

    const eventData = {
        event: 'remove_from_cart',
        ecommerce: {
            currency: 'AUD',
            value: productData.price * productData.quantity,
            items: [productData],
        },
    };

    pushToDataLayer(eventData);

    console.group('🏷️ GTM Event: Remove from Cart');
    console.log('Event Data:', eventData);
    console.log('Timestamp:', new Date().toISOString());
    console.groupEnd();
}

/**
 * Track view cart event
 * COMMENTED OUT - May be added in the future
 */
// export function trackViewCart(cartItems, cartTotal) {
//     console.log('🔍 trackViewCart called', {
//         cartItems,
//         cartTotal,
//         trackingSettings,
//         isEnabled: isTrackingEnabled('view_cart')
//     });
//
//     if (!isTrackingEnabled('view_cart')) {
//         console.warn('⚠️ view_cart tracking is disabled');
//         return;
//     }
//
//     const items = cartItems.map(item => formatProduct(item));
//
//     const eventData = {
//         event: 'view_cart',
//         ecommerce: {
//             currency: 'AUD',
//             value: parseFloat(cartTotal),
//             items: items,
//         },
//     };
//
//     pushToDataLayer(eventData);
//
//     console.group('🏷️ GTM Event: View Cart');
//     console.log('Event Data:', eventData);
//     console.log('Timestamp:', new Date().toISOString());
//     console.groupEnd();
// }

/**
 * Track begin checkout event
 */
export function trackBeginCheckout(cartItems, cartTotal) {
    if (!isTrackingEnabled('begin_checkout')) return;

    const items = cartItems.map(item => formatProduct(item));

    pushToDataLayer({
        event: 'begin_checkout',
        ecommerce: {
            currency: 'AUD',
            value: parseFloat(cartTotal),
            items: items,
        },
    });
}

/**
 * Track add shipping info event
 * COMMENTED OUT - May be added in the future
 */
// export function trackAddShippingInfo(cartItems, cartTotal, shippingTier) {
//     if (!isTrackingEnabled('add_shipping_info')) return;
//
//     const items = cartItems.map(item => formatProduct(item));
//
//     pushToDataLayer({
//         event: 'add_shipping_info',
//         ecommerce: {
//             currency: 'AUD',
//             value: parseFloat(cartTotal),
//             shipping_tier: shippingTier,
//             items: items,
//         },
//     });
// }

/**
 * Track add payment info event
 * COMMENTED OUT - May be added in the future
 */
// export function trackAddPaymentInfo(cartItems, cartTotal) {
//     if (!isTrackingEnabled('add_payment_info')) return;
//
//     const items = cartItems.map(item => formatProduct(item));
//
//     pushToDataLayer({
//         event: 'add_payment_info',
//         ecommerce: {
//             currency: 'AUD',
//             value: parseFloat(cartTotal),
//             payment_type: 'Credit Card',
//             items: items,
//         },
//     });
// }

/**
 * Track select item event (clicking on product in list)
 */
export function trackSelectItem(product, listName, index = 0) {
    if (!isTrackingEnabled('select_item')) return;

    const productData = {
        ...formatProduct(product),
        item_list_name: listName,
        index: index,
    };

    const eventData = {
        event: 'select_item',
        ecommerce: {
            item_list_name: listName,
            items: [productData],
        },
    };

    pushToDataLayer(eventData);

    console.group('🏷️ GTM Event: Select Item');
    console.log('Event Data:', eventData);
    console.log('Timestamp:', new Date().toISOString());
    console.groupEnd();
}

/**
 * Composable export
 */
export function useGTMTracking() {
    return {
        trackAddToCart,
        trackRemoveFromCart,
        // trackViewCart, // Commented out - may be added later
        trackBeginCheckout,
        // trackAddShippingInfo, // Commented out - may be added later
        // trackAddPaymentInfo, // Commented out - may be added later
        trackSelectItem,
        isTrackingEnabled,
    };
}
